import type { DataGridFilterConfig, DataGridFilterRenderContext } from '@ibsys/kit';
import type { ReactNode } from 'react';
import type { ConversationAttributeItem } from '@/entities/attributes';
import { ATTRIBUTE_TYPES, getAttributeTimeRangeFilter } from '@/entities/attributes';
import type { TimeIntervalFormat } from '@/shared/lib/utils/string';
import { NumericColumnFilter, type NumericValueLimits } from '@/shared/ui';
import type { DialogColumnMeta } from './columnMeta';

/**
 * Пределы идентификатора диалога: за ними значение идентификатором быть не может, и поиск
 * отвергает такой отбор как некорректный ввод.
 */
const CONVERSATION_ID_LIMITS: NumericValueLimits = {
  isIntegerOnly: true,
  min: -2147483648,
  max: 2147483647,
};

/** Отбор колонки: встроенный по конфигурации либо свой числовой с двумя режимами */
export type ColumnFilterProps = {
  filter?: DataGridFilterConfig;
  renderFilter?: (ctx: DataGridFilterRenderContext) => ReactNode;
  meta?: DialogColumnMeta;
};

/**
 * Числовой отбор: границы либо перечень значений. Встроенного отбора с двумя режимами у грида
 * нет, поэтому колонка рисует его своим полем, а описание отбора узнаёт его по признаку колонки.
 * Признак заполненности такому полю не достаётся тумблером грида — рядом с ним его рисует
 * само поле.
 */
const numericColumnFilter = (
  limits: NumericValueLimits,
  { enableNotEmptyFilter }: { enableNotEmptyFilter?: boolean } = {},
): ColumnFilterProps => ({
  renderFilter: (ctx) => <NumericColumnFilter ctx={ctx} {...limits} enableNotEmptyFilter={enableNotEmptyFilter} />,
  meta: { hasNumericFilter: true },
});

/** Встроенный отбор по колонке атрибута: к условию по значению добавляется признак заполненности */
const attributeFilter = (filter: DataGridFilterConfig): ColumnFilterProps => ({
  filter: { ...filter, enableNotEmptyFilter: true },
});

/** Отбор колонки «ID»: границы либо перечень идентификаторов */
export const getConversationIdFilterProps = (): ColumnFilterProps => numericColumnFilter(CONVERSATION_ID_LIMITS);

/**
 * Отбор по колонке атрибута: условие по значению — по типу атрибута и действующему формату
 * интервала времени — и признак заполненности рядом с ним. Признак отбирает по наличию значения
 * вместо условия по нему.
 */
export const getAttributeFilterProps = (
  attr: ConversationAttributeItem,
  timeDisplayFormat: TimeIntervalFormat,
): ColumnFilterProps => {
  if (attr.dataType === ATTRIBUTE_TYPES.SELECT || attr.dataType === ATTRIBUTE_TYPES.MULTISELECT) {
    return attributeFilter({
      // Бэкенд принимает и скаляр, и массив (см. SearchConversationFilters.attributes).
      type: 'multiselect',
      options: attr.selectValues?.map((o) => ({ value: o.code, label: o.value })) ?? [],
    });
  }
  if (
    attr.dataType === ATTRIBUTE_TYPES.TIME ||
    attr.dataType === ATTRIBUTE_TYPES.INTEGER ||
    attr.dataType === ATTRIBUTE_TYPES.DECIMAL
  ) {
    // Интервал времени показывается не числом, поэтому и отбирается только границами — тем же
    // общим правилом, что и в классической таблице: расходиться им нельзя
    const timeRange = getAttributeTimeRangeFilter(attr, timeDisplayFormat);
    if (timeRange) return attributeFilter(timeRange);
    return numericColumnFilter(
      { isIntegerOnly: attr.dataType !== ATTRIBUTE_TYPES.DECIMAL },
      { enableNotEmptyFilter: true },
    );
  }
  if (attr.dataType === ATTRIBUTE_TYPES.DATE) return attributeFilter({ type: 'date-range' });
  if (attr.dataType === ATTRIBUTE_TYPES.TIMESTAMP) return attributeFilter({ type: 'timestamp-range' });
  if (attr.dataType === ATTRIBUTE_TYPES.CHECKBOX) return attributeFilter({ type: 'boolean' });
  return attributeFilter({ type: 'text' });
};
